/**
 * Pricing Utilities
 *
 * Shared utilities for formatting prices and billing intervals
 * Used across registration and subscription activation flows
 */

/**
 * Format price amount with currency code
 * @param amount - Amount in cents/smallest currency unit
 * @param currency - Currency code (e.g., 'pln', 'usd')
 * @param fractionDigits - Decimal digits to show (default 0, e.g. "99 PLN"). Pass 2
 *   for amounts that need cents (e.g. a monthly-equivalent "from" price: "82,50 PLN").
 * @returns Formatted price string (e.g., "99 PLN")
 */
export const formatPrice = (
  amount: number,
  currency: string,
  fractionDigits: number = 0
): string => {
  return new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency: currency.toUpperCase(),
    currencyDisplay: "code",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(amount / 100);
};

/**
 * Get human-readable interval name in Polish
 * @param interval - Billing interval ('month' or 'year')
 * @param intervalCount - Number of intervals
 * @returns Polish interval name (e.g., "miesięcznie", "co 3 miesiące")
 */
export const getIntervalName = (
  interval: string,
  intervalCount: number
): string => {
  if (interval === "month") {
    return intervalCount === 1 ? "miesięcznie" : `co ${intervalCount} miesiące`;
  }
  if (interval === "year") {
    return intervalCount === 1 ? "rocznie" : `co ${intervalCount} lata`;
  }
  return interval;
};

/**
 * Get billing period description
 * @param interval - Billing interval ('month' or 'year')
 * @param intervalCount - Number of intervals
 * @returns Billing period description (e.g., "za 1 miesiąc", "za 3 miesiące")
 */
export const getBillingPeriodDescription = (
  interval: string,
  intervalCount: number
): string => {
  if (interval === "month") {
    if (intervalCount === 1) return "za 1 miesiąc";
    if (intervalCount === 3) return "za 3 miesiące";
    return `za ${intervalCount} miesiące`;
  }
  if (interval === "year") {
    return intervalCount === 1 ? "za 1 rok" : `za ${intervalCount} lata`;
  }
  return "";
};
